import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from 'auth';
import { Box, CircularProgress, Typography } from '@mui/material';
import theme from 'theme';
import { getCurrentVacationPeriod, getPreviousVacationPeriod } from './getVacationPeriods';
import { DarkTooltip } from '../../../components/DarkTooltip';

const VacationTilePrevious = ({ user }) => {
  const { tenant } = useAuth();
  const TOTAL_VACATION_DAYS = tenant?.vacation.totalDays;

  const [previousVacationPeriod, setPreviousVacationPeriod] = useState('');
  const [currentVacationPeriod, setCurrentVacationPeriod] = useState('');

  useEffect(() => {
    setPreviousVacationPeriod(getPreviousVacationPeriod());
  }, [setPreviousVacationPeriod]);

  useEffect(() => {
    setCurrentVacationPeriod(getCurrentVacationPeriod());
  }, [setCurrentVacationPeriod]);

  const remainingOldPeriodDays = user.oldVacationDays ?? 0;

  const accessibleRemainingText = `${remainingOldPeriodDays} vacation day${
    remainingOldPeriodDays === 1 ? '' : 's'
  } remaining from previous period`;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0',
          gap: '1.5rem',
          flex: 'none',
          order: '0',
          flexGrow: '0',
        }}
      >
        <Box sx={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              marginTop: theme.spacing(1),
              marginBottom: theme.spacing(1),
              fontWeight: 500,
              fontStyle: 'normal',
              color: '#000000',
            }}
            variant="body1"
          >
            <Box component="span">
              {TOTAL_VACATION_DAYS - remainingOldPeriodDays}
              {TOTAL_VACATION_DAYS - remainingOldPeriodDays === 1 ? ' day ' : ' days '}
              used
            </Box>
          </Typography>
          {user.availableDays < 0 ? (
            <Typography
              sx={{ fontStyle: 'normal', fontWeight: 'normal', color: '#4c4c4c' }}
              variant="body2"
              component="div"
            >
              {currentVacationPeriod}
            </Typography>
          ) : (
            <Typography
              sx={{ fontStyle: 'normal', fontWeight: 'normal', color: '#4c4c4c' }}
              variant="body2"
              component="div"
            >
              {previousVacationPeriod}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          size={75}
          thickness={0.5}
          sx={{ color: remainingOldPeriodDays > 0 ? '#CCCCCC' : 'transparent' }}
          variant="determinate"
          value={((TOTAL_VACATION_DAYS - remainingOldPeriodDays) * 100) / TOTAL_VACATION_DAYS}
          aria-hidden="true"
        />
        <DarkTooltip
          title={
            remainingOldPeriodDays > 0
              ? 'Total number of available days'
              : 'No days left for the old period'
          }
        >
          <Box
            sx={{
              top: 7,
              left: 7,
              bottom: 7,
              right: 7,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: '#F7F7F9',
              cursor: 'pointer',
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                color: remainingOldPeriodDays > 0 ? undefined : '#CCCCCC',
              }}
              variant="subtitle1"
              id="oldVacationDays"
              aria-label={accessibleRemainingText}
            >
              {remainingOldPeriodDays}
            </Typography>
          </Box>
        </DarkTooltip>
      </Box>
    </>
  );
};

VacationTilePrevious.propTypes = {
  user: PropTypes.object,
};

VacationTilePrevious.defaultProps = {
  user: {
    availableDays: 0,
  },
};

export default VacationTilePrevious;
